'use client'
import { envs } from '@config/envs'
import { googleLogout } from '@react-oauth/google'
import { safeLocalStorage } from '@utils/utilities'
import axios from 'axios'

import { hermesLogger, rum } from '../../observability'
import { unleashClient } from '../../unleash/unleash-client'

export type AuthToken = {
  tokenType?: string
  accessToken: string
  refreshToken: string
}

const AUTH_KEY = 'auth'

export class AuthHandler {
  static getTokens = (): { accessToken?: string; refreshToken?: string } => {
    const authStringify = safeLocalStorage.getItem(AUTH_KEY)

    const { accessToken, refreshToken }: Partial<AuthToken> = authStringify
      ? JSON.parse(authStringify)
      : {}

    return { accessToken, refreshToken }
  }

  static get authorization(): string {
    const { accessToken } = AuthHandler.getTokens()

    return `Bearer ${accessToken}`
  }

  static get isAuthenticated(): boolean {
    const { accessToken } = AuthHandler.getTokens()

    return !!accessToken
  }

  static get canRefresh(): boolean {
    const { accessToken, refreshToken } = AuthHandler.getTokens()

    return !!accessToken && !!refreshToken
  }

  static setTokens = (accessToken: string, refreshToken: string) => {
    safeLocalStorage.setItem(
      AUTH_KEY,
      JSON.stringify({ accessToken, refreshToken })
    )
  }

  static clearTokens = () => {
    safeLocalStorage.removeItem(AUTH_KEY)
  }

  static refreshTokens = async (): Promise<string | null> => {
    try {
      const { refreshToken: currentRefreshToken } = AuthHandler.getTokens()

      if (!currentRefreshToken) {
        throw new Error(
          '[AuthHandler] Error refreshing token, refreshToken is not set'
        )
      }

      const { data } = await axios.get<AuthToken>(
        `${envs.cerberusApiUrl}/auth/refresh`,
        {
          headers: {
            'refresh-token': currentRefreshToken,
          },
        }
      )

      const { accessToken } = data

      if (!accessToken)
        throw new Error(`accessToken is not valid ${accessToken}`)

      if (!currentRefreshToken)
        throw new Error(`refreshToken is not valid ${currentRefreshToken}`)

      AuthHandler.setTokens(accessToken, currentRefreshToken)
      return accessToken
    } catch (error) {
      hermesLogger.error('[AuthHandler] Error refreshing token', {
        error,
      })

      await rum.clearUser()
      await unleashClient.stop()
      await AuthHandler.clearTokens()
      await safeLocalStorage.clear()
      await googleLogout()

      window.location.replace('/sign-in')
      return null
    }
  }
}
